import { useRef, useState } from 'react';
import classNames from 'classnames';
import { Button } from 'reactstrap';
import { SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';
import { SurveyStatsFiltersProps, type SurveyStatsStatus } from '../types';
import { SURVEY } from '@constants/terminology';
import { DashboardRow } from '@g17eco/molecules/dashboard';
import type { FilterOptionsMap } from './constants';
import type { DataPeriods } from '@utils/dataPeriods';
import { SubsidiaryGroupSelect } from '@g17eco/molecules/select/SubsidiaryGroupSelect';
import type { InitiativePlain } from '@g17eco/types/initiative';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { type FilterKey, getChangedFilters } from '../utils/filterStorage';
import './SurveyStatsFilters.scss';

export const SAVED_FILTERS_HINT = 'Saved filters only apply to you, in this browser. Other users keep their own.';

interface Props {
  filterOptions: FilterOptionsMap;
  filters: SurveyStatsFiltersProps;
  /** The saved filters, or the defaults when nothing is saved */
  savedFilters: SurveyStatsFiltersProps;
  onMultiSelectChange: (data: Partial<SurveyStatsFiltersProps>) => void;
  onSaveFilters: () => void;
  onResetFilters: () => void;
  /** Results are refetching */
  isUpdating: boolean;
  initiativeId: string;
  subsidiaries: InitiativePlain[];
}

export const SurveyStatsFilters = (props: Props) => {
  const {
    filterOptions,
    filters,
    savedFilters,
    onMultiSelectChange,
    onSaveFilters,
    onResetFilters,
    isUpdating,
    initiativeId,
    subsidiaries,
  } = props;

  const saveButtonRef = useRef<HTMLButtonElement>(null);
  // SubsidiaryGroupSelect derives its checked groups only on mount, so a reset remounts it
  const [resetCount, setResetCount] = useState(0);
  const changedFilters = getChangedFilters(filters, savedFilters);
  const hasChanges = changedFilters.length > 0;

  const handleReset = () => {
    onResetFilters();
    setResetCount((count) => count + 1);
    // The reset button unmounts once nothing has changed
    saveButtonRef.current?.focus();
  };

  const fieldClass = (key: FilterKey) =>
    classNames('w-100', {
      'survey-stats-filters__field--changed': changedFilters.includes(key),
    });

  // An empty filter is not applied, so it shows every value
  const placeholder = (key: FilterKey, label: string, allLabel: string) =>
    filters[key]?.length ? label : allLabel;

  return (
    <DashboardRow classes={{ children: 'flex-lg-column position-relative survey-stats-filters' }}>
      <div className='d-flex gap-2 mb-2'>
        <div className={fieldClass('months')}>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder={placeholder('months', 'Month', 'All months')}
            className='w-100'
            onChange={(values) => onMultiSelectChange({ months: values })}
            options={filterOptions.months}
            values={filters.months || []}
          />
        </div>
        <div className={fieldClass('years')}>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder={placeholder('years', 'Year', 'All years')}
            className='w-100'
            onChange={(values) => onMultiSelectChange({ years: values })}
            options={filterOptions.years}
            values={filters.years || []}
          />
        </div>
        <div className={fieldClass('periods')}>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder={placeholder('periods', 'Period', 'All periods')}
            className='w-100'
            onChange={(values) => onMultiSelectChange({ periods: values as DataPeriods[] })}
            options={filterOptions.periods}
            values={filters.periods || []}
          />
        </div>
      </div>
      <div className='d-flex align-items-center gap-2 mb-2'>
        <div className={fieldClass('surveyStatuses')}>
          <SelectFactory
            selectType={SelectTypes.MultipleSelect}
            placeholder={placeholder('surveyStatuses', `${SURVEY.CAPITALIZED_SINGULAR} Status`, `All ${SURVEY.SINGULAR} statuses`)}
            className='w-100'
            onChange={(values) => onMultiSelectChange({ surveyStatuses: values as SurveyStatsStatus[] })}
            options={filterOptions.surveyStatuses}
            values={filters.surveyStatuses || []}
          />
        </div>
        <div className={fieldClass('initiatives')}>
          <SubsidiaryGroupSelect
            key={resetCount}
            initiativeId={initiativeId}
            subsidiaries={subsidiaries}
            selectedSubsidiaryIds={filters.initiatives || []}
            onChange={(values) => onMultiSelectChange({ initiatives: values })}
            placeholder={placeholder('initiatives', 'Subsidiaries', 'All subsidiaries')}
          />
        </div>
        {/* aria-disabled, not disabled: the button stays focusable, so keyboard focus survives a save */}
        <Button
          innerRef={saveButtonRef}
          color='primary'
          outline={!hasChanges}
          className={classNames('text-nowrap', { disabled: !hasChanges })}
          size='sm'
          aria-disabled={!hasChanges}
          onClick={hasChanges ? onSaveFilters : undefined}
        >
          <i className={classNames('fal mr-2', hasChanges ? 'fa-floppy-disk' : 'fa-check')} />
          {hasChanges ? 'Save filters' : 'Filters saved'}
        </Button>
        {/* Keys remount the tooltip so it binds to the new target */}
        {hasChanges ? (
          <SimpleTooltip key='reset' text='Reset to saved filters' className='d-flex'>
            <Button color='link' size='sm' className='p-0' onClick={handleReset} aria-label='Reset to saved filters'>
              <i className='fal text-lg fa-rotate-left' />
            </Button>
          </SimpleTooltip>
        ) : (
          <SimpleTooltip
            key='info'
            text={SAVED_FILTERS_HINT}
            trigger='hover focus'
            component={
              <i
                className='fal text-lg fa-info-circle text-ThemeIconSecondary'
                role='img'
                tabIndex={0}
                aria-label={SAVED_FILTERS_HINT}
              />
            }
          />
        )}
      </div>
      {isUpdating ? <div className='survey-stats-filters__progress' aria-hidden='true' /> : null}
      {/* Always mounted: screen readers only announce changes to a live region that already exists */}
      <span className='visually-hidden' role='status'>
        {isUpdating ? 'Updating results' : ''}
      </span>
    </DashboardRow>
  );
};
